export interface IPaginationResultFiles<T> {
  totalPages: number;
  totalAssets: number;
  assetsPerPage: number;
  currentPage: number;
  isLastPage: boolean;
  files: T[];
}

export type StatusOptions =
  | 'open'
  | 'importing'
  | 'closed'
  | 'aborted'
  | 'deleted';
export type EtaTextOptions =
  | '1. Processing file'
  | '2. Identifying manufacturers'
  | '3. Identifying products'
  | '4. Checking for errors'
  | '5. Identifying accessories'
  | '6. Adding internal products'
  | '7. Done';

export interface IUser {
  triggeredBy: string | null;
  triggeredByAvatar: string | null;
  triggeredByEmail: string | null;
  triggeredById: string | null;
}

export interface IOnboardingFile {
  id: string;
  fileId: string;
  name: string;
  originalname: string;
  totalItems: number;
  unidentifiedItems: number;
  identifiedItems: number;
  requestItems: number;
  inventoryItems: number;
  discardedItems: number;
  uniqueAssets: {
    discardedItems: number;
    identifiedItems: number;
    inventoryItems: number;
    requestItems: number;
    totalItems: number;
    unidentifiedItems: number;
  };
  uniqueAssetCounts: {
    discardedItems: number;
    identifiedItems: number;
    inventoryItems: number;
    requestItems: number;
    totalItems: number;
    unidentifiedItems: number;
  };
  eta: number;
  etaText: EtaTextOptions;
  status: StatusOptions;
  statusText: string;
  mimetype: string;
  isManual: boolean;
  user: IUser | undefined;
  createdAt: Date;
}

export interface IOnboardingFileApi {
  _id: string;
  fileId: string;
  name: string;
  originalname: string;
  totalItems: number;
  unidentifiedItems: number;
  identifiedItems: number;
  requestItems: number;
  inventoryItems: number;
  discardedItems: number;
  uniqueAssets: {
    discardedItems: number;
    identifiedItems: number;
    inventoryItems: number;
    requestItems: number;
    totalItems: number;
    unidentifiedItems: number;
  };
  uniqueAssetCounts: {
    discardedItems: number;
    identifiedItems: number;
    inventoryItems: number;
    requestItems: number;
    totalItems: number;
    unidentifiedItems: number;
  };
  eta: number;
  etaText: EtaTextOptions;
  status: StatusOptions;
  statusText: string;
  mimetype: string;
  isManual: boolean;
  user: IUser | undefined;
  createdAt: string;
}

export default class Tag implements IOnboardingFile {
  public readonly 'id': string;

  public readonly 'fileId': string;

  public readonly 'name': string;

  public readonly 'originalname': string;

  public readonly 'totalItems': number;

  public readonly 'unidentifiedItems': number;

  public readonly 'identifiedItems': number;

  public readonly 'requestItems': number;

  public readonly 'inventoryItems': number;

  public readonly 'discardedItems': number;

  public readonly 'uniqueAssets': {
    discardedItems: number;
    identifiedItems: number;
    inventoryItems: number;
    requestItems: number;
    totalItems: number;
    unidentifiedItems: number;
  };

  public readonly 'uniqueAssetCounts': {
    discardedItems: number;
    identifiedItems: number;
    inventoryItems: number;
    requestItems: number;
    totalItems: number;
    unidentifiedItems: number;
  };

  public readonly 'eta': number;

  public readonly 'etaText': EtaTextOptions;

  public readonly 'status': StatusOptions;

  public readonly 'statusText': string;

  public readonly 'mimetype': string;

  public readonly 'isManual': boolean;

  public readonly 'user': IUser | undefined;

  public readonly 'createdAt': Date;

  constructor(
    id: string,
    fileId: string,
    name: string,
    originalname: string,
    totalItems: number,
    unidentifiedItems: number,
    identifiedItems: number,
    requestItems: number,
    inventoryItems: number,
    discardedItems: number,
    uniqueAssets: {
      discardedItems: number;
      identifiedItems: number;
      inventoryItems: number;
      requestItems: number;
      totalItems: number;
      unidentifiedItems: number;
    },
    uniqueAssetCounts: {
      discardedItems: number;
      identifiedItems: number;
      inventoryItems: number;
      requestItems: number;
      totalItems: number;
      unidentifiedItems: number;
    },
    eta: number,
    etaText: EtaTextOptions,
    status: StatusOptions,
    statusText: string,
    mimetype: string,
    isManual: boolean,
    user: IUser | undefined,
    createdAt: Date,
  ) {
    this.id = id;
    this.fileId = fileId;
    this.name = name;
    this.originalname = originalname;
    this.totalItems = totalItems;
    this.unidentifiedItems = unidentifiedItems;
    this.identifiedItems = identifiedItems;
    this.requestItems = requestItems;
    this.inventoryItems = inventoryItems;
    this.discardedItems = discardedItems;
    this.uniqueAssets = uniqueAssets;
    this.uniqueAssetCounts = uniqueAssetCounts;
    this.eta = eta;
    this.etaText = etaText;
    this.status = status;
    this.statusText = statusText;
    this.mimetype = mimetype;
    this.isManual = isManual;
    this.user = user;
    this.createdAt = createdAt;
  }

  static fromApiModel(apiModel: IOnboardingFileApi) {
    const {
      _id,
      fileId,
      name,
      discardedItems,
      inventoryItems,
      originalname,
      requestItems,
      status,
      totalItems,
      unidentifiedItems,
      identifiedItems,
      uniqueAssets,
      uniqueAssetCounts,
      eta,
      etaText,
      statusText,
      mimetype,
      isManual,
      user,
    } = apiModel;

    const createdAt = new Date(Date.parse(apiModel.createdAt));

    return new Tag(
      _id,
      fileId,
      name,
      originalname,
      totalItems,
      unidentifiedItems,
      identifiedItems,
      requestItems,
      inventoryItems,
      discardedItems,
      uniqueAssets,
      uniqueAssetCounts,
      eta,
      etaText,
      status,
      statusText,
      mimetype,
      isManual,
      user,
      createdAt,
    );
  }

  static arrayFromApiModel(arrayApiModel: IOnboardingFileApi[]) {
    const array = arrayApiModel.map(apiModel => this.fromApiModel(apiModel));

    return array;
  }

  static arrayPaginatedFromApiModel(
    arrayApiModel: IPaginationResultFiles<IOnboardingFileApi>,
  ) {
    const array = this.arrayFromApiModel(arrayApiModel.files);

    return {
      ...arrayApiModel,
      files: array,
    };
  }
}
